import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

// ─── Modal ──────────────────────────────────────────────────────────────────
/**
 * Every open modal listened for Escape on `window`, so with one modal opened
 * from inside another — previewing a drill diagram while drawing it — a single
 * Escape closed both and threw away the unsaved drawing underneath. Only the
 * topmost modal handles the key now.
 */
const modalStack: symbol[] = [];

/**
 * Join the Escape stack while `open`: only the topmost layer's `onClose` runs.
 * Modal uses it; so does anything else drawn over a modal (the zoom viewer).
 */
export function useEscapeLayer(open: boolean, onClose: () => void): void {
  const idRef = useRef<symbol>(undefined as unknown as symbol);
  if (!idRef.current) idRef.current = Symbol('layer');
  useEffect(() => {
    if (!open) return;
    const id = idRef.current;
    modalStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== id) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = modalStack.lastIndexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
    };
  }, [open, onClose]);
}

export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  size,
  closeButton,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
  /**
   * 'wide' lets the sheet grow past the usual 560px once there is room for it
   * — for drawing surfaces, which are the one thing that gets meaningfully
   * better with more pixels. Below the breakpoint it is still the same
   * full-width bottom sheet, so phones are unaffected.
   */
  size?: 'default' | 'wide' | 'medium';
  /**
   * A ✕ in a header that stays put while the sheet scrolls. For sheets whose
   * content can fill the screen (a drill in run mode), where the strip of
   * backdrop above the sheet is too thin to tap reliably.
   */
  closeButton?: boolean;
}) {
  useEscapeLayer(open, onClose);
  if (!open) return null;
  return (
    <div
      className="modal-bg"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={'modal' + (size === 'wide' ? ' modal-wide' : size === 'medium' ? ' modal-medium' : '')} role="dialog" aria-modal="true">
        {closeButton ? (
          <div className="modal-head">
            <h2>{title}</h2>
            <button type="button" className="btn btn-sm modal-x" onClick={onClose} aria-label="Close">✕</button>
          </div>
        ) : (
          title && <h2>{title}</h2>
        )}
        {children}
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}

// ─── Toast ──────────────────────────────────────────────────────────────────
interface ToastItem {
  id: number;
  msg: string;
  long: boolean;
}
const ToastCtx = createContext<(msg: string, ms?: number) => void>(() => {});
export function useToast() {
  return useContext(ToastCtx);
}
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const show = useCallback((msg: string, ms?: number) => {
    const id = ++seq.current;
    const long = String(msg).length > 42;
    setItems((it) => [...it, { id, msg, long }]);
    setTimeout(() => setItems((it) => it.filter((t) => t.id !== id)), ms ?? (long ? 4200 : 2200));
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toast-host">
        {items.map((t) => (
          <div key={t.id} className={'toast' + (t.long ? ' long' : '')}>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ─── Confirm (replaces window.confirm, which blocks the WebView) ───────────
interface ConfirmReq {
  msg: string;
  ok: string;
  danger: boolean;
  resolve: (v: boolean) => void;
}
const ConfirmCtx = createContext<(msg: string, opts?: { ok?: string; danger?: boolean }) => Promise<boolean>>(
  async () => false,
);
export function useConfirm() {
  return useContext(ConfirmCtx);
}
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<ConfirmReq | null>(null);
  const confirm = useCallback(
    (msg: string, opts?: { ok?: string; danger?: boolean }) =>
      new Promise<boolean>((resolve) => setReq({ msg, ok: opts?.ok ?? 'OK', danger: !!opts?.danger, resolve })),
    [],
  );
  const close = (v: boolean) => {
    req?.resolve(v);
    setReq(null);
  };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal
        open={!!req}
        onClose={() => close(false)}
        actions={
          <>
            <button className="btn" onClick={() => close(false)}>
              Cancel
            </button>
            <button className={'btn ' + (req?.danger ? 'btn-danger' : 'btn-primary')} onClick={() => close(true)}>
              {req?.ok}
            </button>
          </>
        }
      >
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.4 }}>{req?.msg}</div>
      </Modal>
    </ConfirmCtx.Provider>
  );
}

// ─── Segmented control ─────────────────────────────────────────────────────
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button key={o.value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)} type="button">
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Chips ─────────────────────────────────────────────────────────────────
export function ChipToggle({ items, selected, onToggle }: { items: string[]; selected: string[]; onToggle: (v: string) => void }) {
  const sel = useMemo(() => new Set(selected), [selected]);
  return (
    <div className="row row-wrap" style={{ gap: 4 }}>
      {items.map((g) => (
        <button key={g} type="button" className={'chip chip-btn' + (sel.has(g) ? ' on' : '')} onClick={() => onToggle(g)}>
          {g}
        </button>
      ))}
    </div>
  );
}

export function Badge({ bg, fg, children }: { bg: string; fg: string; children: ReactNode }) {
  return (
    <span className="badge" style={{ background: bg, color: fg }}>
      {children}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
